import React, { useRef, useReducer, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Upload, Loader2 } from 'lucide-react';
import { validateImageFile } from '@/@types/photostudio';
import type { ElementType, IUploadFormState, IUploadFormAction } from '@/@types/photostudio';

interface IElementUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeTab: ElementType;
  onSubmit: (
    name: string,
    file: File,
    description?: string
  ) => Promise<{ success: boolean; error?: string }>;
}

const initialState: IUploadFormState = {
  name: '',
  description: '',
  selectedFile: null,
  previewUrl: null,
  isSubmitting: false,
  formError: null,
};

function uploadFormReducer(state: IUploadFormState, action: IUploadFormAction): IUploadFormState {
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, name: action.payload };
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload };
    case 'SET_FILE':
      return {
        ...state,
        selectedFile: action.payload.file,
        previewUrl: action.payload.previewUrl,
        formError: null,
      };
    case 'CLEAR_FILE':
      return { ...state, selectedFile: null, previewUrl: null };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };
    case 'SET_ERROR':
      return { ...state, formError: action.payload };
    case 'RESET':
      return initialState;
  }
}

export function ElementUploadDialog({
  open,
  onOpenChange,
  activeTab,
  onSubmit,
}: IElementUploadDialogProps) {
  const [state, dispatch] = useReducer(uploadFormReducer, initialState);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    dispatch({ type: 'RESET' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [state.previewUrl]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateImageFile(file);
    if (!validation.valid) {
      dispatch({ type: 'SET_ERROR', payload: validation.error || 'Invalid file' });
      return;
    }
    dispatch({
      type: 'SET_FILE',
      payload: { file, previewUrl: URL.createObjectURL(file) },
    });
  }, []);

  const handleCreate = useCallback(async () => {
    if (!state.name.trim() || !state.selectedFile) {
      dispatch({ type: 'SET_ERROR', payload: 'Name and image are required' });
      return;
    }
    dispatch({ type: 'SET_SUBMITTING', payload: true });
    const result = await onSubmit(
      state.name.trim(),
      state.selectedFile,
      state.description.trim() || undefined
    );
    dispatch({ type: 'SET_SUBMITTING', payload: false });
    if (result.success) {
      onOpenChange(false);
      resetForm();
    } else {
      dispatch({ type: 'SET_ERROR', payload: result.error || 'Failed to create element' });
    }
  }, [state.name, state.selectedFile, state.description, onSubmit, onOpenChange, resetForm]);

  const handleClearFile = useCallback(() => {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    dispatch({ type: 'CLEAR_FILE' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [state.previewUrl]);

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        onOpenChange(o);
        if (!o) resetForm();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Add New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Image</Label>
            {state.previewUrl ? (
              <div className="relative aspect-square overflow-hidden rounded-lg border bg-muted">
                <img src={state.previewUrl} alt="Preview" className="size-full object-cover" />
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute right-2 top-2"
                  onClick={handleClearFile}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div
                role="button"
                tabIndex={0}
                className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-muted/50 hover:bg-muted/80"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <Upload className="mb-2 size-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click to upload image</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="element-name">Name</Label>
            <Input
              id="element-name"
              value={state.name}
              onChange={e => dispatch({ type: 'SET_NAME', payload: e.target.value })}
              placeholder={`My ${activeTab}`}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="element-description">
              Description
              <span className="ml-1 text-xs text-muted-foreground font-normal">
                (auto-generated if left empty)
              </span>
            </Label>
            <Textarea
              id="element-description"
              value={state.description}
              onChange={e => dispatch({ type: 'SET_DESCRIPTION', payload: e.target.value })}
              placeholder="e.g., red cotton t-shirt with crew neck"
              maxLength={500}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              A good description helps AI understand exactly what to use from this reference.
            </p>
          </div>

          {state.formError && <p className="text-sm text-destructive">{state.formError}</p>}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={state.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!state.name.trim() || !state.selectedFile || state.isSubmitting}
          >
            {state.isSubmitting ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Plus className="mr-2 size-4" />
            )}
            Add Element
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
